import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ArrowRight, Mail, Phone } from 'lucide-react';
import Logo from './ui/Logo';
import ThemeToggle from './ui/ThemeToggle';

const NAV_LINKS = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  { name: 'Services', to: '/services' },
  { name: 'Projects', to: '/projects' },
  { name: 'Contact', to: '/contact' },
];

const CONTACT = {
  email: 'info@vidhuratechnologies.com',
  phone: '+91 7842091427',
};

/*
 * Sticky glass navbar - transparent at the top of the page, switches to
 * a blurred glass surface after 20px of scroll. Mobile drawer slides in
 * from the top and closes on route change.
 */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/70 dark:bg-ink-950/60 border-b border-ink-200/60 dark:border-white/10'
          : 'bg-transparent'
      }`}
    >
      {/* Utility bar - small, hidden when scrolled to save vertical space. */}
      <div
        className={`hidden md:block overflow-hidden transition-all duration-500 ${
          scrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
        }`}
      >
        <div className="container-x flex h-9 items-center justify-end gap-5 text-xs text-ink-500 dark:text-ink-300">
          <a
            href={`mailto:${CONTACT.email}`}
            className="inline-flex items-center gap-1.5 hover:text-ink-900 dark:hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            {CONTACT.email}
          </a>
          <a
            href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 hover:text-ink-900 dark:hover:text-white transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            {CONTACT.phone}
          </a>
        </div>
      </div>

      <nav className="container-x flex h-24 md:h-28 items-center justify-between">
        <Link to="/" aria-label="Vidhura Technologies - Home" className="shrink-0">
          <Logo height={80} />
        </Link>

        <ul className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `relative px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-ink-900 dark:text-white'
                      : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.name}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                        className="absolute inset-0 -z-10 rounded-full bg-ink-100 dark:bg-white/10"
                      />
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" />
          <Link to="/contact" className="hidden md:inline-flex btn-primary !px-5 !py-2 text-sm">
            Start a project
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-ink-200/60 dark:border-white/10 bg-white/90 dark:bg-ink-950/80 backdrop-blur-xl"
          >
            <ul className="container-x py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === '/'}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 text-base font-medium ${
                        isActive
                          ? 'bg-ink-100 dark:bg-white/10 text-ink-900 dark:text-white'
                          : 'text-ink-600 dark:text-ink-300'
                      }`
                    }
                  >
                    {l.name}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2 flex items-center justify-between">
                <ThemeToggle />
                <Link to="/contact" className="btn-primary !px-5 !py-2 text-sm">
                  Start a project
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
